// Two-step verification (ADR-063, task 387.11.6).
//
// Sign-in steps are anonymous: the challenge token from the login response is the authority, and a completed step opens
// the session (refresh cookie) exactly like login. The "my factors" calls and step-up are ordinary signed-in calls.

import { api } from '../client';
import { sessionApi } from '../session';
import type { ConsoleSession, MfaFactorOption } from '../contracts/session';
import type { SwitchTenantResponse } from './auth';

export const MFA_METHOD_LABEL: Record<string, string> = {
  TOTP: 'Authenticator app',
  WEBAUTHN: 'Passkey',
  EMAIL_OTP: 'Email code',
};

/** One way to answer a challenge. */
export interface MfaAnswer {
  factorId?: string;
  code?: string;
  recoveryCode?: string;
  /** JSON.stringify of the browser's passkey assertion. */
  passkeyResponse?: string;
}

export interface TotpEnrolment {
  factorId: string;
  secret: string;
  otpauthUri: string;
}

export interface EmailEnrolment {
  factorId: string;
  sentTo: string;
  /** Signed-in enrolment only. */
  enrolmentToken?: string | null;
}

export interface PasskeyRegistrationStart {
  optionsJson: string;
  /** Signed-in enrolment only. */
  enrolmentToken?: string | null;
}

export interface EnrolmentSessionResult {
  session: ConsoleSession;
  recoveryCodes?: string[] | null;
}

export interface EnrolmentResult {
  recoveryCodes?: string[] | null;
}

export interface MyMfaState {
  factors: MfaFactorOption[];
  recoveryCodesRemaining: number;
  required: boolean;
  allowedMethods: string[];
}

export interface OperatorMfaSetting {
  required: boolean;
  availableMethods: string[];
}

// ── sign-in (anonymous, challenge token) ─────────────────────────────────────────────────────────────────────────────

export const signInMfa = {
  verify: (challengeToken: string, answer: MfaAnswer) =>
    sessionApi.post<ConsoleSession>('api/console/auth/mfa/verify', { challengeToken, ...answer }),
  sendEmailCode: (challengeToken: string, factorId: string) =>
    sessionApi.post<{ sentTo: string; expiresAt: string }>('api/console/auth/mfa/email/send', {
      challengeToken,
      factorId,
    }),
  passkeyOptions: (challengeToken: string) =>
    sessionApi.post<{ optionsJson: string }>('api/console/auth/mfa/passkey/options', {
      challengeToken,
    }),

  totpStart: (challengeToken: string) =>
    sessionApi.post<TotpEnrolment>('api/console/auth/mfa/enrol/totp/start', { challengeToken }),
  totpConfirm: (challengeToken: string, factorId: string, code: string, label?: string) =>
    sessionApi.post<EnrolmentSessionResult>('api/console/auth/mfa/enrol/totp/confirm', {
      challengeToken,
      factorId,
      code,
      label,
    }),
  emailStart: (challengeToken: string) =>
    sessionApi.post<EmailEnrolment>('api/console/auth/mfa/enrol/email/start', { challengeToken }),
  emailConfirm: (challengeToken: string, factorId: string, code: string) =>
    sessionApi.post<EnrolmentSessionResult>('api/console/auth/mfa/enrol/email/confirm', {
      token: challengeToken,
      factorId,
      code,
    }),
  passkeyRegistrationOptions: (challengeToken: string) =>
    sessionApi.post<PasskeyRegistrationStart>('api/console/auth/mfa/enrol/passkey/options', {
      challengeToken,
    }),
  passkeyRegistrationComplete: (
    challengeToken: string,
    attestationResponse: string,
    label?: string,
  ) =>
    sessionApi.post<EnrolmentSessionResult>('api/console/auth/mfa/enrol/passkey/complete', {
      token: challengeToken,
      attestationResponse,
      label,
    }),
};

// ── signed in ────────────────────────────────────────────────────────────────────────────────────────────────────────

export const myMfa = {
  state: () => api.get<MyMfaState>('api/console/auth/mfa/factors'),
  remove: (factorId: string) => api.del(`api/console/auth/mfa/factors/${factorId}`),
  regenerateRecoveryCodes: () => api.post<string[]>('api/console/auth/mfa/recovery-codes'),

  totpStart: () => api.post<TotpEnrolment>('api/console/auth/mfa/factors/totp/start'),
  totpConfirm: (factorId: string, code: string, label?: string) =>
    api.post<EnrolmentResult>('api/console/auth/mfa/factors/totp/confirm', {
      json: { factorId, code, label },
    }),
  emailStart: () => api.post<EmailEnrolment>('api/console/auth/mfa/factors/email/start'),
  emailConfirm: (enrolmentToken: string, factorId: string, code: string) =>
    api.post<EnrolmentResult>('api/console/auth/mfa/factors/email/confirm', {
      json: { token: enrolmentToken, factorId, code },
    }),
  passkeyRegistrationOptions: () =>
    api.post<PasskeyRegistrationStart>('api/console/auth/mfa/factors/passkey/options'),
  passkeyRegistrationComplete: (
    enrolmentToken: string,
    attestationResponse: string,
    label?: string,
  ) =>
    api.post<EnrolmentResult>('api/console/auth/mfa/factors/passkey/complete', {
      json: { token: enrolmentToken, attestationResponse, label },
    }),

  /** Answers a step-up raised by switch-tenant; the response completes the switch. */
  stepUp: (challengeToken: string, answer: MfaAnswer) =>
    api.post<SwitchTenantResponse>('api/console/auth/mfa/step-up/verify', {
      json: { challengeToken, ...answer },
    }),
};

// ── administration ───────────────────────────────────────────────────────────────────────────────────────────────────

export const adminMfa = {
  /** 204: the person's factors and recovery codes are revoked and their sessions end. */
  resetUser: (userId: string) =>
    api.post<unknown>(`api/admin/users/${userId}/mfa/reset`).then(() => undefined),
  getPlatform: () => api.get<OperatorMfaSetting>('api/admin/security/mfa'),
  setRequired: (required: boolean) =>
    api.put<OperatorMfaSetting>('api/admin/security/mfa', { json: { required } }),
};
